import React, { createContext, useContext, useState, useEffect, useRef, useReducer, useCallback } from 'react';
import { initBuzzer, playBuzzer } from '../sounds/buzzer';
import { validLicenses } from '../config/licenses';
import { MediaProvider } from './MediaContext';

const ScoreboardContext = createContext();
const broadcastChannel = new BroadcastChannel('scoreboard-sync');

const gameReducer = (state, action) => {
  switch (action.type) {
    case 'START_GAME':
      return { ...state, isRunning: true };
    case 'STOP_GAME':
      return { ...state, isRunning: false };
    case 'SET_TIME':
      return { ...state, timeInSeconds: action.payload };
    case 'DECREMENT_TIME':
      return { ...state, timeInSeconds: state.timeInSeconds - 1 };
    case 'SET_PERIOD':
      return { ...state, period: action.payload };
    case 'RESET_TIMEOUT_COUNTERS':
      return { ...state, homeTimeoutCount: 0, awayTimeoutCount: 0 };
    case 'PERIOD_ENDED':
      const [minutes, seconds] = action.payload.split(':').map(Number);
      const newTime = minutes * 60 + seconds;
      return {
        ...state,
        isRunning: false,
        timeInSeconds: newTime
      };
    case 'RESET_GAME':
      const [resetMin, resetSec] = action.payload.split(':').map(Number);
      return {
        ...state,
        isRunning: false,
        period: 1,
        timeInSeconds: resetMin * 60 + resetSec
      };
    default:
      return state;
  }
};

export const ScoreboardProvider = ({ children }) => {
  // Load initial state from localStorage
  const loadInitialState = () => {
    try {
      const savedState = localStorage.getItem('scoreboard-state');
      if (savedState) {
        const parsed = JSON.parse(savedState);
        return {
          homeTeam: parsed.homeTeam,
          awayTeam: parsed.awayTeam,
          gameState: parsed.gameState,
          homePenalties: parsed.homePenalties,
          awayPenalties: parsed.awayPenalties,
          homeTeamInferiorityTime: parsed.homeTeamInferiorityTime || 0,
          awayTeamInferiorityTime: parsed.awayTeamInferiorityTime || 0,
          backgroundColor: parsed.backgroundColor,
          defaultGameDuration: parsed.defaultGameDuration,
          defaultOvertime: parsed.defaultOvertime,
          carouselImages: parsed.carouselImages,
          activeTimeout: parsed.activeTimeout,
          homeTimeoutCount: parsed.homeTimeoutCount || 0,
          awayTimeoutCount: parsed.awayTimeoutCount || 0,
          showTimeoutOverlay: parsed.showTimeoutOverlay,
          showSettings: parsed.showSettings,
          defaultBreakDuration: parsed.defaultBreakDuration,
          overtimeBreak1Duration: parsed.overtimeBreak1Duration,
          overtimeBreak2Duration: parsed.overtimeBreak2Duration,
          autoBreakEnabled: parsed.autoBreakEnabled,
          showBreakOverlay: parsed.showBreakOverlay,
          isBreakActive: parsed.isBreakActive,
          breakTimeInSeconds: parsed.breakTimeInSeconds,
          isBuzzerEnabled: parsed.isBuzzerEnabled,
          buzzerVolume: parsed.buzzerVolume,
          isEntertainmentEnabled: parsed.isEntertainmentEnabled,
          activeLicense: parsed.activeLicense,
          homeTimeoutCount: parsed.homeTimeoutCount || 0,
          awayTimeoutCount: parsed.awayTimeoutCount || 0,
          showTeamInferiority: parsed.showTeamInferiority ?? false,
        };
      }
    } catch (error) {
      console.warn('Error loading state from localStorage:', error);
    }
    return null;
  };

  const initialState = loadInitialState();

  const [homeTeam, setHomeTeam] = useState(initialState?.homeTeam || {
    name: 'HOME TEAM',
    score: 0,
    fouls: 0,
    hasTimeout: false
  });
  
  const [awayTeam, setAwayTeam] = useState(initialState?.awayTeam || {
    name: 'AWAY TEAM',
    score: 0,
    fouls: 0,
    hasTimeout: false
  });

  const [homePenalties, setHomePenalties] = useState(initialState?.homePenalties || []);
  const [awayPenalties, setAwayPenalties] = useState(initialState?.awayPenalties || []);
  const [homeTeamInferiorityTime, setHomeTeamInferiorityTime] = useState(initialState?.homeTeamInferiorityTime || 0);
  const [awayTeamInferiorityTime, setAwayTeamInferiorityTime] = useState(initialState?.awayTeamInferiorityTime || 0);
  const homeInferiorityStartRef = useRef(0);
  const awayInferiorityStartRef = useRef(0);
  const homeInferiorityInitialRef = useRef(0);
  const awayInferiorityInitialRef = useRef(0);
  const [backgroundColor, setBackgroundColor] = useState(() => {
    return initialState?.backgroundColor || localStorage.getItem('backgroundColor') || '#1a374d';
  });
  const [defaultGameDuration, setDefaultGameDuration] = useState(() => {
    return initialState?.defaultGameDuration || localStorage.getItem('defaultGameDuration') || '25:00';
  });
  const [defaultOvertime, setDefaultOvertime] = useState(() => {
    return initialState?.defaultOvertime || localStorage.getItem('defaultOvertime') || '05:00';
  });
  const [carouselImages, setCarouselImages] = useState(() => {
    if (initialState?.carouselImages) return initialState.carouselImages;
    try {
      const saved = localStorage.getItem('carouselImages');
      return saved && saved !== 'undefined' ? JSON.parse(saved) : [];
    } catch (error) {
      console.warn('Error parsing carouselImages from localStorage:', error);
      return [];
    }
  });
  const [showSettings, setShowSettings] = useState(initialState?.showSettings || false);
  const [showEntertainmentSettings, setShowEntertainmentSettings] = useState(false);
  const [activeTimeout, setActiveTimeout] = useState(initialState?.activeTimeout || null);
  const [homeTimeoutCount, setHomeTimeoutCount] = useState(initialState?.homeTimeoutCount || 0);
  const [awayTimeoutCount, setAwayTimeoutCount] = useState(initialState?.awayTimeoutCount || 0);
  const [showTimeoutOverlay, setShowTimeoutOverlay] = useState(initialState?.showTimeoutOverlay || false);
  // Timeout timing (analog to PersonalPenalties) using local refs
  const timeoutStartRef = useRef(0);
  const timeoutInitialRef = useRef(60);
  const [defaultBreakDuration, setDefaultBreakDuration] = useState(initialState?.defaultBreakDuration || '10:00');
  const [overtimeBreak1Duration, setOvertimeBreak1Duration] = useState(initialState?.overtimeBreak1Duration || '03:00');
  const [overtimeBreak2Duration, setOvertimeBreak2Duration] = useState(initialState?.overtimeBreak2Duration || '02:00');
  const [autoBreakEnabled, setAutoBreakEnabled] = useState(() => {
    return initialState?.autoBreakEnabled ?? (localStorage.getItem('autoBreakEnabled') === 'true');
  });
  const [showBreakOverlay, setShowBreakOverlay] = useState(initialState?.showBreakOverlay || false);
  const [isBreakActive, setIsBreakActive] = useState(initialState?.isBreakActive || false);
  const [breakTimeInSeconds, setBreakTimeInSeconds] = useState(initialState?.breakTimeInSeconds || 600); // 10 minutes
  const [showBreakSelectionModal, setShowBreakSelectionModal] = useState(false);
  const [isBuzzerEnabled, setIsBuzzerEnabled] = useState(() => {
    return initialState?.isBuzzerEnabled ?? localStorage.getItem('isBuzzerEnabled') !== 'false';
  });
  const [buzzerVolume, setBuzzerVolume] = useState(() => {
    return initialState?.buzzerVolume ?? (parseFloat(localStorage.getItem('buzzerVolume')) || 0.5);
  });
  const [isEntertainmentEnabled, setIsEntertainmentEnabled] = useState(() => {
    return initialState?.isEntertainmentEnabled || localStorage.getItem('isEntertainmentEnabled') === 'true';
  });
  const [activeLicense, setActiveLicense] = useState(() => {
    return initialState?.activeLicense || localStorage.getItem('activeLicense') || '';
  });
  const [showTeamInferiority, setShowTeamInferiority] = useState(() => {
    return initialState?.showTeamInferiority ?? (localStorage.getItem('showTeamInferiority') === 'true');
  });



  const [gameState, dispatch] = useReducer(gameReducer, initialState?.gameState || {
    isRunning: false,
    period: 1,
    timeInSeconds: (() => {
      const [minutes, seconds] = (initialState?.defaultGameDuration || defaultGameDuration).split(':').map(Number);
      return minutes * 60 + seconds;
    })()
  });

  const [centralTick, setCentralTick] = useState(0);
  const tickInterval = useRef(null);
  const gameStartTime = useRef(0);
  const lastTimeUpdate = useRef(0);
  const initialTimeRef = useRef(0);  // Store initial time when game starts
  const [penalties, setPenalties] = useState({});  // Store all penalty timers here

  const isMainWindow = useRef(window.opener === null);

  // Reset team inferior times on resetAll event
  useEffect(() => {
    const handleReset = () => {
      setHomeTeamInferiorityTime(0);
      setAwayTeamInferiorityTime(0);
      homeInferiorityStartRef.current = 0;
      awayInferiorityStartRef.current = 0;
      homeInferiorityInitialRef.current = 0;
      awayInferiorityInitialRef.current = 0;
    };
    window.addEventListener('resetAll', handleReset);
    return () => window.removeEventListener('resetAll', handleReset);
  }, []);

  useEffect(() => {
    let intervalId;
    if (gameState.isRunning && isMainWindow.current) {
      const startTime = Date.now();
      const initialTime = gameState.timeInSeconds;
      
      intervalId = setInterval(() => {
        const elapsedSeconds = Math.floor((Date.now() - startTime) / 1000);
        const newTime = Math.max(0, initialTime - elapsedSeconds);
        
        if (newTime !== gameState.timeInSeconds) {
          // First update local state
          dispatch({ type: 'SET_TIME', payload: newTime });
          
          // Then broadcast to all windows
          broadcastChannel.postMessage({
            type: 'TICK_UPDATE',
            payload: { timeInSeconds: newTime }
          });
          
          // Only stop the game when time reaches 0
          if (newTime === 0) {
            dispatch({ type: 'STOP_GAME' });
            broadcastChannel.postMessage({
              type: 'GAME_STATE_UPDATE',
              payload: { isRunning: false }
            });
          }
        }
      }, 100);
    }
    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [gameState.isRunning]);

  // Handle period changes only when game is stopped
  useEffect(() => {
    if (isMainWindow.current && !gameState.isRunning && gameState.timeInSeconds === 0) {
      // Add a small delay to ensure all state updates are processed
      const timeoutId = setTimeout(() => {
        // Check if we should advance to the next period
        let shouldAdvance = false;
        
        if (gameState.period === 1) {
          // Always advance from period 1 to period 2
          shouldAdvance = true;
        } else if (gameState.period === 2) {
          // Only advance to period 3 (overtime) if score is tied
          shouldAdvance = homeTeam.score === awayTeam.score;
        } else if (gameState.period === 3) {
          // Only advance to period 4 (second overtime) if score is still tied
          shouldAdvance = homeTeam.score === awayTeam.score;
        }
        
        if (shouldAdvance && gameState.period < 4) {
          const nextPeriod = gameState.period + 1;
          const nextTime = nextPeriod <= 2 ? defaultGameDuration : defaultOvertime;
          const nextTimeInSeconds = nextTime.split(':').reduce((acc, curr) => acc * 60 + parseInt(curr), 0);
          
          // Broadcast changes first
          broadcastChannel.postMessage({
            type: 'GAME_STATE_UPDATE',
            payload: {
              period: nextPeriod,
              timeInSeconds: nextTimeInSeconds,
              isOvertime: nextPeriod > 2
            }
          });
          
          // Then update local state
          dispatch({ type: 'SET_PERIOD', payload: nextPeriod });
          dispatch({ type: 'SET_TIME', payload: nextTimeInSeconds });
          
          // Start break overlay with appropriate duration based on next period (only if autoBreakEnabled)
          let breakDuration = null;
          if (autoBreakEnabled) {
            if (nextPeriod === 2) {
              breakDuration = defaultBreakDuration; // Between period 1 and 2
            } else if (nextPeriod === 3) {
              breakDuration = overtimeBreak1Duration; // Between period 2 and 3
            } else if (nextPeriod === 4) {
              breakDuration = overtimeBreak2Duration; // Between period 3 and 4
            }
          }
          
          if (breakDuration) {
            const [breakMinutes, breakSeconds] = breakDuration.split(':').map(Number);
            const breakTimeSeconds = breakMinutes * 60 + breakSeconds;
            setBreakTimeInSeconds(breakTimeSeconds);
            setShowBreakOverlay(true);
            setIsBreakActive(true);
            
            // Broadcast break overlay state
            broadcastChannel.postMessage({
              type: 'BREAK_UPDATE',
              payload: {
                showBreakOverlay: true,
                isBreakActive: true,
                breakTimeInSeconds: breakTimeSeconds,
                defaultBreakDuration: breakDuration
              }
            });
          }
          
          // Broadcast a message to indicate overtime has started
          if (nextPeriod === 3) {
            broadcastChannel.postMessage({
              type: 'OVERTIME_START',
              payload: {
                overtimePeriod: 1,
                timeInSeconds: nextTimeInSeconds
              }
            });
          } else if (nextPeriod === 4) {
            broadcastChannel.postMessage({
              type: 'OVERTIME_START',
              payload: {
                overtimePeriod: 2,
                timeInSeconds: nextTimeInSeconds
              }
            });
          }
        }
      }, 100);
      
      return () => clearTimeout(timeoutId);
    }
  }, [gameState.isRunning, gameState.timeInSeconds, gameState.period, homeTeam.score, awayTeam.score, defaultGameDuration, defaultOvertime, defaultBreakDuration, overtimeBreak1Duration, overtimeBreak2Duration, autoBreakEnabled, setBreakTimeInSeconds, setShowBreakOverlay, setIsBreakActive]);

  useEffect(() => {
    const handleMessage = (event) => {
      if (!isMainWindow.current) {
        const { type, payload } = event.data;
        switch (type) {
          case 'TICK_UPDATE':
            dispatch({ type: 'SET_TIME', payload: payload.timeInSeconds });
            break;
          case 'GAME_STATE_UPDATE':
            if (payload.timeInSeconds !== undefined) {
              dispatch({ type: 'SET_TIME', payload: payload.timeInSeconds });
            }
            if (payload.isRunning !== undefined) {
              dispatch({ type: payload.isRunning ? 'START_GAME' : 'STOP_GAME' });
            }
            if (payload.period !== undefined) {
              dispatch({ type: 'SET_PERIOD', payload: payload.period });
            }
            break;
          case 'OVERTIME_START':
            // Handle overtime specific logic in live view
            dispatch({ type: 'SET_TIME', payload: payload.timeInSeconds });
            break;
          case 'TEAM_UPDATE':
            if (payload.team === 'home') {
              setHomeTeam(payload.data);
            } else {
              setAwayTeam(payload.data);
            }
            break;
          case 'PENALTIES_UPDATE':
            if (payload.team === 'home') {
              setHomePenalties(payload.penalties);
            } else {
              setAwayPenalties(payload.penalties);
            }
            break;
          case 'TEAM_INFERIOR_UPDATE':
            if (payload.team === 'home') {
              setHomeTeamInferiorityTime(payload.inferiorTime);
            } else {
              setAwayTeamInferiorityTime(payload.inferiorTime);
            }
            break;
          case 'TIMEOUT_UPDATE':
            setActiveTimeout(payload.activeTimeout);
            setShowTimeoutOverlay(payload.showTimeoutOverlay);
            break;
          case 'BREAK_UPDATE':
            setShowBreakOverlay(payload.showBreakOverlay);
            setIsBreakActive(payload.isBreakActive);
            setBreakTimeInSeconds(payload.breakTimeInSeconds);
            setDefaultBreakDuration(payload.defaultBreakDuration);
            break;
          case 'TIMEOUT_END':
            handleTimeoutEnd();
            break;
          case 'RESET_GAME':
            const [resetMin, resetSec] = payload.split(':').map(Number);
            dispatch({ type: 'RESET_GAME', payload: `${resetMin}:${resetSec}` });
            break;
        }
      }
    };

    broadcastChannel.addEventListener('message', handleMessage);
    return () => {
      broadcastChannel.removeEventListener('message', handleMessage);
    };
  }, []);

  // Initialize timeout start on overlay open
  useEffect(() => {
    if (isMainWindow.current && showTimeoutOverlay && activeTimeout) {
      if (timeoutStartRef.current === 0) {
        timeoutStartRef.current = Date.now();
        timeoutInitialRef.current = activeTimeout.timeLeft ?? 60;
      }
    } else if (!showTimeoutOverlay) {
      // Reset refs when overlay closes
      timeoutStartRef.current = 0;
    }
  }, [showTimeoutOverlay, activeTimeout]);

  useEffect(() => {
    if (isMainWindow.current) {
      broadcastChannel.postMessage({
        type: 'GAME_STATE_UPDATE',
        payload: {
          isRunning: gameState.isRunning,
          period: gameState.period,
          timeInSeconds: gameState.timeInSeconds
        }
      });
    }
  }, [gameState.isRunning, gameState.period]);

  useEffect(() => {
    if (isMainWindow.current) {
      broadcastChannel.postMessage({
        type: 'TEAM_UPDATE',
        payload: {
          team: 'home',
          data: homeTeam
        }
      });
    }
  }, [homeTeam]);

  useEffect(() => {
    if (isMainWindow.current) {
      broadcastChannel.postMessage({
        type: 'TEAM_UPDATE',
        payload: {
          team: 'away',
          data: awayTeam
        }
      });
    }
  }, [awayTeam]);

  useEffect(() => {
    if (isMainWindow.current) {
      broadcastChannel.postMessage({
        type: 'PENALTIES_UPDATE',
        payload: {
          team: 'home',
          penalties: homePenalties
        }
      });
    }
  }, [homePenalties]);

  useEffect(() => {
    if (isMainWindow.current) {
      broadcastChannel.postMessage({
        type: 'PENALTIES_UPDATE',
        payload: {
          team: 'away',
          penalties: awayPenalties
        }
      });
    }
  }, [awayPenalties]);

  useEffect(() => {
    if (isMainWindow.current) {
      broadcastChannel.postMessage({
        type: 'TEAM_INFERIOR_UPDATE',
        payload: {
          team: 'home',
          inferiorTime: homeTeamInferiorityTime
        }
      });
    }
  }, [homeTeamInferiorityTime]);

  useEffect(() => {
    if (isMainWindow.current) {
      broadcastChannel.postMessage({
        type: 'TEAM_INFERIOR_UPDATE',
        payload: {
          team: 'away',
          inferiorTime: awayTeamInferiorityTime
        }
      });
    }
  }, [awayTeamInferiorityTime]);


  useEffect(() => {
    if (isMainWindow.current) {
      broadcastChannel.postMessage({
        type: 'TIMEOUT_UPDATE',
        payload: {
          activeTimeout,
          showTimeoutOverlay
        }
      });
    }
  }, [activeTimeout, showTimeoutOverlay]);

  useEffect(() => {
    if (isMainWindow.current) {
      broadcastChannel.postMessage({
        type: 'BREAK_UPDATE',
        payload: {
          showBreakOverlay,
          isBreakActive,
          breakTimeInSeconds,
          defaultBreakDuration
        }
      });
    }
  }, [showBreakOverlay, isBreakActive, breakTimeInSeconds, defaultBreakDuration]);

  useEffect(() => {
    if (isMainWindow.current) {
      broadcastChannel.postMessage({
        type: 'GAME_STATE_UPDATE',
        payload: {
          isRunning: gameState.isRunning,
          period: gameState.period,
          timeInSeconds: gameState.timeInSeconds
        }
      });
    }
  }, [gameState.isRunning, gameState.period, gameState.timeInSeconds]);

  // Format time in MM:SS format
  const formatTime = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${String(minutes).padStart(2, '0')}:${String(remainingSeconds).padStart(2, '0')}`;
  };

  // Calculate elapsed time for the entire game
  const calculateElapsedTime = () => {
    const currentTime = calculateCurrentTime();
    const [defaultMinutes, defaultSeconds] = defaultGameDuration.split(':').map(Number);
    const [overtimeMinutes, overtimeSeconds] = defaultOvertime.split(':').map(Number);
    const defaultTimeInSeconds = defaultMinutes * 60 + defaultSeconds;
    const overtimeInSeconds = overtimeMinutes * 60 + overtimeSeconds;
    
    if (gameState.period <= 2) {
      // For periods 1 and 2: period * defaultGameTime + elapsed time
      return (gameState.period - 1) * defaultTimeInSeconds + (defaultTimeInSeconds - currentTime);
    } else {
      // For periods 3 and 4: 2 * defaultGameTime + (period-2) * defaultOvertime + elapsed time
      const overtimePeriod = gameState.period - 2;
      return (2 * defaultTimeInSeconds) + 
             ((overtimePeriod-1) * overtimeInSeconds) + 
             (overtimeInSeconds - currentTime);
    }
  };

  // Calculate current game time based on real time without state updates
  const calculateCurrentTime = () => {
    if (!gameState.isRunning || gameStartTime.current === 0) {
      return gameState.timeInSeconds;
    }
    const now = Date.now();
    const elapsedMs = now - gameStartTime.current;
    const elapsedSeconds = Math.floor(elapsedMs / 1000);
    return Math.max(0, initialTimeRef.current - elapsedSeconds);
  };

  // Calculate current penalty time based on real time
  const calculatePenaltyTime = (penaltyId) => {
    const penalty = penalties[penaltyId];
    if (!penalty || !penalty.isActive || penalty.startTime === 0) {
      return penalty?.timeLeft || 0;
    }
    const now = Date.now();
    const elapsedMs = now - penalty.startTime;
    const elapsedSeconds = Math.floor(elapsedMs / 1000);
    return Math.max(0, penalty.initialTime - elapsedSeconds);
  };

  // Get current inferiority time (accounting for countdown if game is running)
  const getCurrentInferiorityTime = (team) => {
    if (team === 'home') {
      if (!gameState.isRunning || homeInferiorityStartRef.current === 0) {
        return homeTeamInferiorityTime;
      }
      const now = Date.now();
      const elapsedSeconds = Math.floor((now - homeInferiorityStartRef.current) / 1000);
      return Math.max(0, homeInferiorityInitialRef.current - elapsedSeconds);
    } else {
      if (!gameState.isRunning || awayInferiorityStartRef.current === 0) {
        return awayTeamInferiorityTime;
      }
      const now = Date.now();
      const elapsedSeconds = Math.floor((now - awayInferiorityStartRef.current) / 1000);
      return Math.max(0, awayInferiorityInitialRef.current - elapsedSeconds);
    }
  };

  // Central tick mechanism with time updates
  useEffect(() => {
    const updateTimes = () => {
      setCentralTick(prev => prev + 1);
      
      if (gameState.isRunning) {
        const currentTime = calculateCurrentTime();
        if (currentTime !== gameState.timeInSeconds) {
          lastTimeUpdate.current = Date.now();
          dispatch({ type: 'SET_TIME', payload: currentTime });
          
          if (currentTime === 0) {
            const nextPeriodTime = gameState.period >= 2 ? defaultOvertime : defaultGameDuration;
            dispatch({ type: 'PERIOD_ENDED', payload: nextPeriodTime });
          }
        }
      }

      // Update penalty timers
      Object.entries(penalties).forEach(([id, penalty]) => {
        if (penalty.isActive) {
          const currentTime = calculatePenaltyTime(id);
          if (currentTime !== penalty.timeLeft) {
            setPenalties(prev => ({
              ...prev,
              [id]: {
                ...prev[id],
                timeLeft: currentTime,
                lastUpdate: Date.now()
              }
            }));

            // Stop penalty when it reaches 0
            if (currentTime === 0) {
              setPenalties(prev => ({
                ...prev,
                [id]: {
                  ...prev[id],
                  isActive: false,
                  timeLeft: 0
                }
              }));
            }
          }
        }
      });

      // Update team inferiority times during game - countdown based on real time
      // Continue counting down even with only 1 penalty (for when we add another penalty)
      if (gameState.isRunning) {
        if (homeTeamInferiorityTime > 0 || homeInferiorityInitialRef.current > 0) {
          const newHomeInferiorTime = getCurrentInferiorityTime('home');
          if (newHomeInferiorTime !== homeTeamInferiorityTime) {
            setHomeTeamInferiorityTime(newHomeInferiorTime);
            if (newHomeInferiorTime === 0) {
              homeInferiorityStartRef.current = 0;
              homeInferiorityInitialRef.current = 0;
            }
          }
        }
        if (awayTeamInferiorityTime > 0 || awayInferiorityInitialRef.current > 0) {
          const newAwayInferiorTime = getCurrentInferiorityTime('away');
          if (newAwayInferiorTime !== awayTeamInferiorityTime) {
            setAwayTeamInferiorityTime(newAwayInferiorTime);
            if (newAwayInferiorTime === 0) {
              awayInferiorityStartRef.current = 0;
              awayInferiorityInitialRef.current = 0;
            }
          }
        }
      }

      // Update timeout remaining in main window and broadcast via state
      if (isMainWindow.current && showTimeoutOverlay && activeTimeout) {
        if (timeoutStartRef.current === 0) {
          timeoutStartRef.current = Date.now();
          timeoutInitialRef.current = activeTimeout.timeLeft ?? 60;
        }
        const elapsed = Math.floor((Date.now() - timeoutStartRef.current) / 1000);
        const remaining = Math.max(0, (timeoutInitialRef.current || 60) - elapsed);
        if (remaining !== (activeTimeout.timeLeft ?? 60)) {
          setActiveTimeout(prev => prev ? { ...prev, timeLeft: remaining } : prev);
        }
        if (remaining === 0) {
          // Mirror end button logic on expiry
          playBuzzer();
          // Clear overlay and active timeout
          setShowTimeoutOverlay(false);
          const team = activeTimeout.team;
          setActiveTimeout(null);
          // Reset team flag like the button does
          if (team === 'home') {
            setHomeTeam(prev => ({ ...prev, hasTimeout: false }));
          } else if (team === 'away') {
            setAwayTeam(prev => ({ ...prev, hasTimeout: false }));
          }
          // Reset refs
          timeoutStartRef.current = 0;
        }
      }
    };

    // Use a more reasonable interval for UI updates (100ms)
    tickInterval.current = setInterval(updateTimes, 100);
    return () => clearInterval(tickInterval.current);
  }, [gameState.isRunning, gameState.timeInSeconds, gameState.period, defaultOvertime, defaultGameDuration, penalties, showTimeoutOverlay, activeTimeout, homePenalties, awayPenalties, homeTeamInferiorityTime, awayTeamInferiorityTime]);

  // Handle game start/stop
  useEffect(() => {
    if (gameState.isRunning) {
      if (gameStartTime.current === 0) {
        // Immediate start
        const now = Date.now();
        gameStartTime.current = now;
        lastTimeUpdate.current = now;
        initialTimeRef.current = gameState.timeInSeconds;  // Store initial time when starting
        
        // Start team inferior timers if they have time
        if (homeTeamInferiorityTime > 0) {
          homeInferiorityStartRef.current = now;
          homeInferiorityInitialRef.current = homeTeamInferiorityTime;
        }
        if (awayTeamInferiorityTime > 0) {
          awayInferiorityStartRef.current = now;
          awayInferiorityInitialRef.current = awayTeamInferiorityTime;
        }
      }
    } else if (gameStartTime.current !== 0) {
      // Stop game and update final time
      const finalTime = calculateCurrentTime();
      dispatch({ type: 'SET_TIME', payload: finalTime });
      gameStartTime.current = 0;
      lastTimeUpdate.current = 0;
      initialTimeRef.current = 0;  // Reset initial time reference
      
      // Update team inferiority times to current values when stopping
      if (homeTeamInferiorityTime > 0) {
        const currentHomeTime = getCurrentInferiorityTime('home');
        setHomeTeamInferiorityTime(currentHomeTime);
        homeInferiorityStartRef.current = 0;
        homeInferiorityInitialRef.current = currentHomeTime;
      }
      if (awayTeamInferiorityTime > 0) {
        const currentAwayTime = getCurrentInferiorityTime('away');
        setAwayTeamInferiorityTime(currentAwayTime);
        awayInferiorityStartRef.current = 0;
        awayInferiorityInitialRef.current = currentAwayTime;
      }
    }
  }, [gameState.isRunning]);

  // Start a new penalty timer
  const startPenaltyTimer = (penaltyId, initialTime) => {
    const now = Date.now();
    setPenalties(prev => ({
      ...prev,
      [penaltyId]: {
        initialTime,
        timeLeft: initialTime,
        isActive: true,
        startTime: now,
        lastUpdate: now
      }
    }));
  };

  // Stop a penalty timer
  const stopPenaltyTimer = (penaltyId) => {
    setPenalties(prev => {
      const penalty = prev[penaltyId];
      if (!penalty) return prev;
      return {
        ...prev,
        [penaltyId]: {
          ...penalty,
          isActive: false,
          timeLeft: calculatePenaltyTime(penaltyId)
        }
      };
    });
  };

  // Reset a penalty timer
  const resetPenaltyTimer = (penaltyId) => {
    setPenalties(prev => {
      const penalty = prev[penaltyId];
      if (!penalty) return prev;
      return {
        ...prev,
        [penaltyId]: {
          ...penalty,
          isActive: false,
          timeLeft: penalty.initialTime,
          startTime: 0,
          lastUpdate: 0
        }
      };
    });
  };

  // Start a manual break with selected duration
  const startManualBreak = useCallback((breakType) => {
    let breakDuration = null;
    
    if (breakType === 'p1-p2') {
      breakDuration = defaultBreakDuration;
    } else if (breakType === 'p2-p3') {
      breakDuration = overtimeBreak1Duration;
    } else if (breakType === 'p3-p4') {
      breakDuration = overtimeBreak2Duration;
    }
    
    if (breakDuration) {
      const [breakMinutes, breakSeconds] = breakDuration.split(':').map(Number);
      const breakTimeSeconds = breakMinutes * 60 + breakSeconds;
      setBreakTimeInSeconds(breakTimeSeconds);
      setShowBreakOverlay(true);
      setIsBreakActive(true);
      setShowBreakSelectionModal(false);
      
      // Broadcast break overlay state
      broadcastChannel.postMessage({
        type: 'BREAK_UPDATE',
        payload: {
          showBreakOverlay: true,
          isBreakActive: true,
          breakTimeInSeconds: breakTimeSeconds,
          defaultBreakDuration: breakDuration
        }
      });
    }
  }, [defaultBreakDuration, overtimeBreak1Duration, overtimeBreak2Duration]);

  // Calculate team inferior time based on penalties (for initial calculation)
  const calculateTeamInferiorTime = (teamPenalties) => {
    if (teamPenalties.length === 0) return 0;
    
    // Sort penalties by remaining time (descending)
    const sortedPenalties = [...teamPenalties].sort((a, b) => b.timeInSeconds - a.timeInSeconds);
    
    if (sortedPenalties.length === 1) {
      return sortedPenalties[0].timeInSeconds;
    } else if (sortedPenalties.length === 2) {
      // For two penalties: current time + new penalty time
      return sortedPenalties[0].timeInSeconds + sortedPenalties[1].timeInSeconds;
    } else if (sortedPenalties.length >= 3) {
      // For three or more penalties: sum all penalty times
      return sortedPenalties.reduce((sum, penalty) => sum + penalty.timeInSeconds, 0);
    }
    
    return 0;
  };

  // Update team inferiority time based on penalty changes
  const updateTeamInferiorityTime = (team, penalties, changeInSeconds = null) => {
    const now = Date.now();
    const isHome = team === 'home';
    
    // Get current inferiority time (with countdown if running)
    const currentInferiorityTime = getCurrentInferiorityTime(team);
    
    console.log('[updateTeamInferiorityTime]', {
      team,
      penaltiesCount: penalties.length,
      changeInSeconds,
      currentInferiorityTime
    });
    
    let newInferiorityTime = 0;
    
    // Apply rules based on penalty count and change type
    if (penalties.length === 0) {
      // Rule: No penalties → Inferiority = 0
      newInferiorityTime = 0;
    } else if (penalties.length === 1 && changeInSeconds !== null) {
      // Rule: Single penalty with change → Add change to current time
      newInferiorityTime = Math.max(0, currentInferiorityTime + changeInSeconds);
    } else if (penalties.length >= 2 && changeInSeconds !== null) {
      // Rule: 2+ penalties with change → Add change to current time
      newInferiorityTime = Math.max(0, currentInferiorityTime + changeInSeconds);
    } else {
      // No change specified → Keep current time (continues counting down)
      newInferiorityTime = currentInferiorityTime;
    }
    
    console.log('[updateTeamInferiorityTime] Result:', {
      newInferiorityTime,
      willUpdateRefs: changeInSeconds !== null || penalties.length === 0
    });
    
    // Update state and refs
    if (isHome) {
      setHomeTeamInferiorityTime(newInferiorityTime);
      if (penalties.length === 0) {
        homeInferiorityStartRef.current = 0;
        homeInferiorityInitialRef.current = 0;
      } else if (changeInSeconds !== null) {
        // Reset refs when there's a change
        homeInferiorityStartRef.current = gameState.isRunning ? now : 0;
        homeInferiorityInitialRef.current = newInferiorityTime;
      }
    } else {
      setAwayTeamInferiorityTime(newInferiorityTime);
      if (penalties.length === 0) {
        awayInferiorityStartRef.current = 0;
        awayInferiorityInitialRef.current = 0;
      } else if (changeInSeconds !== null) {
        // Reset refs when there's a change
        awayInferiorityStartRef.current = gameState.isRunning ? now : 0;
        awayInferiorityInitialRef.current = newInferiorityTime;
      }
    }
  };

  const handleTimeoutEnd = () => {
    // End timeout and clear flags like the button logic
    const team = activeTimeout?.team;
    setShowTimeoutOverlay(false);
    setActiveTimeout(null);
    if (team === 'home') {
      setHomeTeam(prev => ({ ...prev, hasTimeout: false }));
    } else if (team === 'away') {
      setAwayTeam(prev => ({ ...prev, hasTimeout: false }));
    }
    timeoutStartRef.current = 0;
  };

  // Initialize buzzer sound
  useEffect(() => {
    initBuzzer();
  }, []);

  // Update localStorage when buzzer settings change
  useEffect(() => {
    localStorage.setItem('isBuzzerEnabled', isBuzzerEnabled);
  }, [isBuzzerEnabled]);

  useEffect(() => {
    localStorage.setItem('buzzerVolume', buzzerVolume.toString());
  }, [buzzerVolume]);

  useEffect(() => {
    localStorage.setItem('autoBreakEnabled', autoBreakEnabled.toString());
  }, [autoBreakEnabled]);

  // Track previous states
  const prevGameRunningRef = useRef(false);
  const prevActiveTimeoutRef = useRef(null);
  const prevPeriodRef = useRef(1);
  const prevTimeoutOverlayRef = useRef(false);

  // Play buzzer when timer reaches zero, game is paused, or timeout is requested
  useEffect(() => {
    // Case 1: Timer reaches zero
    if (gameState.timeInSeconds === 0) {
      playBuzzer();
      return;
    }

    // Case 2: Game is paused while timeout is active, or timeout requested while game is paused
    if ((prevGameRunningRef.current && !gameState.isRunning && activeTimeout) || 
        (!gameState.isRunning && !prevActiveTimeoutRef.current && activeTimeout)) {
      playBuzzer();
    }

    // Update previous states
    prevGameRunningRef.current = gameState.isRunning;
    prevActiveTimeoutRef.current = activeTimeout;
  }, [gameState.timeInSeconds, gameState.isRunning, activeTimeout]);

  // Effect to handle timeout counter updates (count ONCE per start)
  useEffect(() => {
    if (!isMainWindow.current) return;
    const wasOpen = prevTimeoutOverlayRef.current;
    const isOpen = showTimeoutOverlay === true;
    // Rising edge: overlay just opened for a team
    if (!wasOpen && isOpen && activeTimeout?.team) {
      if (activeTimeout.team === 'home') {
        setHomeTimeoutCount(prev => prev + 1);
      } else if (activeTimeout.team === 'away') {
        setAwayTimeoutCount(prev => prev + 1);
      }
    }
    prevTimeoutOverlayRef.current = isOpen;
  }, [showTimeoutOverlay, activeTimeout?.team]);

  // Effect to reset timeout counters when period changes
  useEffect(() => {
    if (gameState.period !== prevPeriodRef.current) {
      setHomeTimeoutCount(0);
      setAwayTimeoutCount(0);
      prevPeriodRef.current = gameState.period;
    }
  }, [gameState.period]);

  const validateLicense = (licenseInput) => {
    const [name, key] = licenseInput.split(':');
    if (!name || !key) return false;
    
    return validLicenses[name] === key;
  };

  const activateLicense = (licenseInput) => {
    if (validateLicense(licenseInput)) {
      setActiveLicense(licenseInput);
      setIsEntertainmentEnabled(true);
      localStorage.setItem('activeLicense', licenseInput);
      localStorage.setItem('isEntertainmentEnabled', 'true');
      return true;
    }
    return false;
  };

  const deactivateLicense = () => {
    setActiveLicense('');
    setIsEntertainmentEnabled(false);
    localStorage.removeItem('activeLicense');
    localStorage.setItem('isEntertainmentEnabled', 'false');
  };

  // Save state to localStorage whenever it changes
  useEffect(() => {
    const state = {
      homeTeam,
      awayTeam,
      gameState,
      homePenalties,
      awayPenalties,
      homeTeamInferiorityTime,
      awayTeamInferiorityTime,
      backgroundColor,
      defaultGameDuration,
      defaultOvertime,
      carouselImages,
      activeTimeout,
      showTimeoutOverlay,
      showSettings,
      showEntertainmentSettings,
      defaultBreakDuration,
      overtimeBreak1Duration,
      overtimeBreak2Duration,
      autoBreakEnabled,
      showBreakOverlay,
      isBreakActive,
      breakTimeInSeconds,
      isBuzzerEnabled,
      buzzerVolume,
      isEntertainmentEnabled,
      activeLicense,
      homeTimeoutCount,
      awayTimeoutCount,
      showTeamInferiority
    };
    localStorage.setItem('scoreboard-state', JSON.stringify(state));
  }, [
    homeTeam,
    awayTeam,
    gameState,
    homePenalties,
    awayPenalties,
    homeTeamInferiorityTime,
    awayTeamInferiorityTime,
    backgroundColor,
    defaultGameDuration,
    defaultOvertime,
    carouselImages,
    activeTimeout,
    showTimeoutOverlay,
    showSettings,
    showEntertainmentSettings,
    defaultBreakDuration,
    overtimeBreak1Duration,
    overtimeBreak2Duration,
    autoBreakEnabled,
    showBreakOverlay,
    isBreakActive,
    breakTimeInSeconds,
    isBuzzerEnabled,
    buzzerVolume,
    isEntertainmentEnabled,
    activeLicense,
    homeTimeoutCount,
    awayTimeoutCount,
    showTeamInferiority
  ]);

  const value = {
    homeTeam,
    setHomeTeam,
    awayTeam,
    setAwayTeam,
    backgroundColor,
    setBackgroundColor,
    defaultGameDuration,
    setDefaultGameDuration,
    defaultOvertime,
    setDefaultOvertime,
    carouselImages,
    setCarouselImages,
    showSettings,
    setShowSettings,
    showEntertainmentSettings,
    setShowEntertainmentSettings,
    activeTimeout,
    setActiveTimeout,
    showTimeoutOverlay,
    setShowTimeoutOverlay,
    defaultBreakDuration,
    setDefaultBreakDuration,
    overtimeBreak1Duration,
    setOvertimeBreak1Duration,
    overtimeBreak2Duration,
    setOvertimeBreak2Duration,
    autoBreakEnabled,
    setAutoBreakEnabled,
    showBreakOverlay,
    setShowBreakOverlay,
    isBreakActive,
    setIsBreakActive,
    breakTimeInSeconds,
    setBreakTimeInSeconds,
    showBreakSelectionModal,
    setShowBreakSelectionModal,
    startManualBreak,
    homePenalties,
    setHomePenalties,
    awayPenalties,
    setAwayPenalties,
    homeTeamInferiorityTime,
    setHomeTeamInferiorityTime,
    awayTeamInferiorityTime,
    setAwayTeamInferiorityTime,
    timeInSeconds: gameState.timeInSeconds,
    period: gameState.period,
    isGameRunning: gameState.isRunning,
    dispatch,
    centralTick,
    calculateCurrentTime,
    calculateElapsedTime,
    formatTime,
    penalties,
    setPenalties,
    calculatePenaltyTime,
    gameState,
    startPenaltyTimer,
    stopPenaltyTimer,
    resetPenaltyTimer,
    handleTimeoutEnd,
    isBuzzerEnabled,
    setIsBuzzerEnabled,
    buzzerVolume,
    setBuzzerVolume,
    isEntertainmentEnabled,
    activeLicense,
    activateLicense,
    deactivateLicense,
    homeTimeoutCount,
    setHomeTimeoutCount,
    awayTimeoutCount,
    setAwayTimeoutCount,
    calculateTeamInferiorTime,
    updateTeamInferiorityTime,
    showTeamInferiority,
    setShowTeamInferiority,
  };

  return (
    <ScoreboardContext.Provider value={value}>
      <MediaProvider isGameRunning={gameState.isRunning}>
        {children}
      </MediaProvider>
    </ScoreboardContext.Provider>
  );
};

export const useScoreboard = () => {
  const context = useContext(ScoreboardContext);
  if (!context) {
    throw new Error('useScoreboard must be used within a ScoreboardProvider');
  }
  return context;
};
